import type {
  IdentifyRequest,
  IdentifyResponse,
  IdentifyPhotoRequest,
  IdentifyPhotoResponse,
  LookalikeRequest,
  LookalikeResponse,
  RecategorizeRequest,
  RecategorizeResponse,
  PlaceNamesRequest,
  PlaceNamesResponse,
  GeoSearchResponse,
  PlaceResult,
  FactsheetRequest,
  Factsheet,
  SpeciesResponse,
  BuddyListResponse,
  BuddyInviteRequest,
  BuddyInviteResponse,
  BuddySpeciesResponse,
  ProfileUpdateRequest,
  SharedTripResponse,
  TripShareResponse,
  TripMembersResponse,
  TripSharedSightingsResponse,
  TripLogInvitesResponse,
  PendingTripMembershipsResponse,
  AuthUser,
  BuddyLifelistResponse,
  Species,
} from "../../shared/types";

export type { PlaceResult } from "../../shared/types";

async function readError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string };
    if (body && typeof body.error === "string") return body.error;
  } catch {
    /* fall through */
  }
  return `Request failed (${res.status}).`;
}

// Typed API error so callers can distinguish rate-limit hits (429) from
// other failures without string-matching the message. Preserves the
// server's Retry-After hint when set.
export class ApiError extends Error {
  status: number;
  retryAfter?: number;
  constructor(message: string, status: number, retryAfter?: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.retryAfter = retryAfter;
  }
}

async function apiError(res: Response): Promise<ApiError> {
  const msg = await readError(res);
  const raw = res.headers.get("Retry-After");
  const retryAfter = raw ? Number(raw) : NaN;
  const err = new ApiError(
    msg,
    res.status,
    Number.isFinite(retryAfter) ? retryAfter : undefined,
  );
  let endpoint = "";
  try {
    endpoint = new URL(res.url).pathname;
  } catch {
    endpoint = res.url ?? "";
  }
  try {
    const { logEvent } = await import("./events");
    if (res.status === 429) {
      logEvent("ratelimit.hit", {
        endpoint,
        retry_after: err.retryAfter ?? 0,
      });
    } else {
      // Every non-429 failure gets an api.error event with status +
      // message, so callers that only toast the failure still leave a
      // searchable trail in the debug timeline.
      logEvent("api.error", {
        endpoint,
        status: res.status,
        message: msg,
      });
    }
  } catch {
    /* logging is best-effort */
  }
  return err;
}

export interface SpeciesQuery {
  location?: string;
  lat?: number;
  lng?: number;
  name?: string;
  radiusKm: number;
}

// Bump when the server's species response shape, sort, or filter changes
// in a way that should bypass the browser's HTTP cache for /api/species.
// The browser key includes the full URL, so a new value here gets a fresh
// network fetch instead of the cached (private, max-age=86400) response.
const SPECIES_CACHE_VERSION = "8";

export async function fetchSpecies(
  q: SpeciesQuery,
): Promise<SpeciesResponse> {
  const params = new URLSearchParams();
  params.set("radius_km", String(q.radiusKm));
  if (q.lat !== undefined && q.lng !== undefined) {
    params.set("lat", String(q.lat));
    params.set("lng", String(q.lng));
    if (q.name) params.set("name", q.name);
  } else if (q.location) {
    params.set("location", q.location);
  }
  params.set("v", SPECIES_CACHE_VERSION);

  const res = await fetch(`/api/species?${params.toString()}`);
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as SpeciesResponse;
}

// Place autocomplete for the location dropdown. Best-effort: resolves to []
// on any failure (including an aborted request) so typing never throws.
//
// In-flight and short-lived response dedup: typing "Kona" fires K→Ko→
// Kon→Kona which used to be four requests, three of which the user
// immediately abandoned. Dedup by query so a repeat / rapid-fire keystroke
// returns the pending promise (or the last-cached response for the same
// text). See geosearch-client-no-inflight-dedupe.
const GEOSEARCH_TTL_MS = 30_000;
const geosearchCache = new Map<string, { ts: number; value: PlaceResult[] }>();
const geosearchInFlight = new Map<string, Promise<PlaceResult[]>>();
export async function geosearch(
  query: string,
  signal?: AbortSignal,
): Promise<PlaceResult[]> {
  const key = query.trim().toLowerCase();
  const cached = geosearchCache.get(key);
  if (cached && Date.now() - cached.ts < GEOSEARCH_TTL_MS) return cached.value;
  const existing = geosearchInFlight.get(key);
  if (existing) return existing;
  const pending = (async () => {
    try {
      const res = await fetch(
        `/api/geosearch?q=${encodeURIComponent(query)}`,
        { signal },
      );
      if (!res.ok) return [];
      const body = (await res.json()) as GeoSearchResponse;
      const results = Array.isArray(body.results) ? body.results : [];
      geosearchCache.set(key, { ts: Date.now(), value: results });
      if (geosearchCache.size > 20) {
        const oldest = geosearchCache.keys().next().value;
        if (oldest !== undefined) geosearchCache.delete(oldest);
      }
      return results;
    } catch {
      return [];
    } finally {
      geosearchInFlight.delete(key);
    }
  })();
  geosearchInFlight.set(key, pending);
  return pending;
}

export async function identify(
  payload: IdentifyRequest,
): Promise<IdentifyResponse> {
  const res = await fetch("/api/identify", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as IdentifyResponse;
}

export async function identifyPhoto(
  payload: IdentifyPhotoRequest,
): Promise<IdentifyPhotoResponse> {
  const res = await fetch("/api/identify-photo", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as IdentifyPhotoResponse;
}

export async function lookalikes(
  payload: LookalikeRequest,
): Promise<LookalikeResponse> {
  const res = await fetch("/api/lookalikes", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as LookalikeResponse;
}

export async function recategorize(
  payload: RecategorizeRequest,
): Promise<RecategorizeResponse> {
  const res = await fetch("/api/recategorize", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as RecategorizeResponse;
}

export async function placeNames(
  payload: PlaceNamesRequest,
): Promise<PlaceNamesResponse> {
  const res = await fetch("/api/placenames", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as PlaceNamesResponse;
}

export async function factsheet(
  payload: FactsheetRequest,
): Promise<Factsheet> {
  const res = await fetch("/api/factsheet", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as Factsheet;
}

export async function identifyVideo(
  payload: {
    frames: string[];
    location_name: string;
    candidates: { common: string; scientific: string; count: number }[];
  },
): Promise<{
  matches: import("../../shared/types").IdentifyMatch[];
  meta: import("../../shared/types").IdentifyMeta;
  frames_analysed: number;
}> {
  const res = await fetch("/api/identify-video", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await apiError(res);
  return res.json();
}

export interface ReferralMe {
  code: string;
  signup_count: number;
}
export async function getMyReferral(): Promise<ReferralMe> {
  const res = await fetch("/api/referrals/me");
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as ReferralMe;
}

// Off-list logging — resolve a scientific name (typically one the
// identify LLM returned that wasn't in the location's candidate list)
// to a full Species so it can be added to the lifelist. Throws on
// 404 / other errors so the caller can surface a toast.
export async function lookupSpecies(
  scientific_name: string,
): Promise<Species> {
  const res = await fetch("/api/species/lookup", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ scientific_name }),
  });
  if (!res.ok) throw await apiError(res);
  const body = (await res.json()) as { species: Species };
  return body.species;
}

// "Wrong photo?" swap — asks the server for a Wikipedia summary photo
// for the given scientific name. Returns "" when Wikipedia has no
// suitable image (no article, disambiguation page, transient failure);
// caller shows a toast in that case.
export interface PhotoSwapResult {
  photo_url: string;
  reason?: "no-result" | "error";
}
export async function swapSpeciesPhoto(
  scientific_name: string,
): Promise<PhotoSwapResult> {
  const res = await fetch("/api/species/photo-swap", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ scientific_name }),
  });
  if (!res.ok) throw await apiError(res);
  const body = (await res.json()) as PhotoSwapResult;
  return { photo_url: body.photo_url ?? "", reason: body.reason };
}

// ---- Buddies ----

export async function updateProfile(
  payload: ProfileUpdateRequest,
): Promise<AuthUser> {
  const res = await fetch("/api/auth/profile", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await apiError(res);
  const body = (await res.json()) as { user: AuthUser };
  return body.user;
}

export async function listBuddies(): Promise<BuddyListResponse> {
  const res = await fetch("/api/buddies");
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as BuddyListResponse;
}

export async function inviteBuddy(
  payload: BuddyInviteRequest,
): Promise<BuddyInviteResponse> {
  const res = await fetch("/api/buddies/invite", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as BuddyInviteResponse;
}

export async function acceptBuddyInvite(payload: {
  invite_id?: string;
  token?: string;
}): Promise<void> {
  const res = await fetch("/api/buddies/accept", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await apiError(res);
}

export async function rejectBuddyInvite(inviteId: string): Promise<void> {
  const res = await fetch("/api/buddies/reject", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ invite_id: inviteId }),
  });
  if (!res.ok) throw await apiError(res);
}

export async function cancelBuddyInvite(inviteId: string): Promise<void> {
  const res = await fetch("/api/buddies/cancel", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ invite_id: inviteId }),
  });
  if (!res.ok) throw await apiError(res);
}

export async function removeBuddy(userId: string): Promise<void> {
  const res = await fetch(`/api/buddies/${encodeURIComponent(userId)}`, {
    method: "DELETE",
  });
  if (!res.ok) throw await apiError(res);
}

export async function blockBuddy(userId: string): Promise<void> {
  const res = await fetch("/api/buddies/block", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ user_id: userId }),
  });
  if (!res.ok) throw await apiError(res);
}

export async function unblockBuddy(userId: string): Promise<void> {
  const res = await fetch("/api/buddies/unblock", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ user_id: userId }),
  });
  if (!res.ok) throw await apiError(res);
}

// Buddy-species lookups back a SpeciesDetail-only view — the network cost
// per open is small, but users routinely bounce between the same handful
// of species in one session. A tiny in-memory cache keyed by normalized
// name with a 5-minute TTL drops repeat opens to zero API calls without
// hiding fresh buddy sightings for more than a few minutes at worst.
const BUDDY_SPECIES_TTL_MS = 5 * 60 * 1000;
const buddySpeciesCache = new Map<
  string,
  { ts: number; value: BuddySpeciesResponse }
>();

export async function buddiesForSpecies(
  scientificName: string,
): Promise<BuddySpeciesResponse> {
  const key = scientificName.trim().toLowerCase();
  const hit = buddySpeciesCache.get(key);
  if (hit && Date.now() - hit.ts < BUDDY_SPECIES_TTL_MS) return hit.value;
  const res = await fetch(
    `/api/buddies/species?scientific_name=${encodeURIComponent(scientificName)}`,
  );
  if (!res.ok) throw await apiError(res);
  const value = (await res.json()) as BuddySpeciesResponse;
  buddySpeciesCache.set(key, { ts: Date.now(), value });
  return value;
}

/** Invalidate the buddies-per-species cache — call after buddy add/remove. */
export function invalidateBuddiesForSpecies(): void {
  buddySpeciesCache.clear();
}

/** Fetch a buddy's full lifelist so you can browse their collection. */
export async function buddyLifelist(
  userId: string,
): Promise<BuddyLifelistResponse> {
  const res = await fetch(
    `/api/buddies/${encodeURIComponent(userId)}/lifelist`,
  );
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as BuddyLifelistResponse;
}

// ---- Trip URLs ----

export async function getTripShare(tripId: string): Promise<TripShareResponse> {
  const res = await fetch(`/api/trips/${encodeURIComponent(tripId)}/share`);
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as TripShareResponse;
}

export async function createOrRotateTripShare(
  tripId: string,
  mode: "public" | "buddies",
): Promise<TripShareResponse> {
  const res = await fetch(`/api/trips/${encodeURIComponent(tripId)}/share`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ mode }),
  });
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as TripShareResponse;
}

export async function revokeTripShare(tripId: string): Promise<void> {
  const res = await fetch(`/api/trips/${encodeURIComponent(tripId)}/share`, {
    method: "DELETE",
  });
  if (!res.ok) throw await apiError(res);
}

export async function getSharedTrip(
  slug: string,
): Promise<SharedTripResponse> {
  const res = await fetch(`/api/trip-share/${encodeURIComponent(slug)}`);
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as SharedTripResponse;
}

// ---- Trip members / shared logging ----

export async function getTripMembers(
  tripId: string,
): Promise<TripMembersResponse> {
  const res = await fetch(`/api/trips/${encodeURIComponent(tripId)}/members`);
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as TripMembersResponse;
}

export async function inviteTripMember(
  tripId: string,
  payload: {
    username?: string;
    user_id?: string;
    /** When true, on accept the server fans out log-invites for every
     *  past sighting on this trip so the new member gets offered them
     *  as pending "add to your list?" prompts. */
    backfill?: boolean;
  },
): Promise<void> {
  const res = await fetch(`/api/trips/${encodeURIComponent(tripId)}/invite`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await apiError(res);
}

export interface AcceptTripInviteResult {
  /** Number of past-sightings log-invites fanned out to me on accept,
   *  when the sender enabled retroactive share. Zero on the normal
   *  (no-backfill) path. */
  backfilled: number;
}
export async function acceptTripInvite(
  tripId: string,
): Promise<AcceptTripInviteResult> {
  const res = await fetch(`/api/trips/${encodeURIComponent(tripId)}/accept`, {
    method: "POST",
  });
  if (!res.ok) throw await apiError(res);
  const body = (await res.json().catch(() => ({}))) as {
    backfilled?: number;
  };
  return { backfilled: Number(body.backfilled) || 0 };
}

export async function leaveTrip(tripId: string): Promise<void> {
  const res = await fetch(`/api/trips/${encodeURIComponent(tripId)}/leave`, {
    method: "POST",
  });
  if (!res.ok) throw await apiError(res);
}

export async function removeTripMember(
  tripId: string,
  memberId: string,
): Promise<void> {
  const res = await fetch(
    `/api/trips/${encodeURIComponent(tripId)}/members/${encodeURIComponent(memberId)}`,
    { method: "DELETE" },
  );
  if (!res.ok) throw await apiError(res);
}

export async function logToSharedTrip(
  tripId: string,
  payload: {
    taxon_id: number;
    scientific_name: string;
    common_name?: string;
    photo_url?: string;
    location_name?: string;
  },
): Promise<void> {
  const res = await fetch(`/api/trips/${encodeURIComponent(tripId)}/log`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await apiError(res);
}

export async function getTripSharedSightings(
  tripId: string,
): Promise<TripSharedSightingsResponse> {
  const res = await fetch(`/api/trips/${encodeURIComponent(tripId)}/shared`);
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as TripSharedSightingsResponse;
}

export async function getTripLogInvites(): Promise<TripLogInvitesResponse> {
  const res = await fetch("/api/trip-log-invites");
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as TripLogInvitesResponse;
}

export async function acceptTripLogInvite(id: string): Promise<void> {
  const res = await fetch(
    `/api/trip-log-invites/${encodeURIComponent(id)}/accept`,
    { method: "POST" },
  );
  if (!res.ok) throw await apiError(res);
}

export async function dismissTripLogInvite(id: string): Promise<void> {
  const res = await fetch(
    `/api/trip-log-invites/${encodeURIComponent(id)}/dismiss`,
    { method: "POST" },
  );
  if (!res.ok) throw await apiError(res);
}

export async function getPendingTripMemberships(): Promise<PendingTripMembershipsResponse> {
  const res = await fetch("/api/trips/-/pending-invites");
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as PendingTripMembershipsResponse;
}
